import type { Lottery, LotteryPrize, LotteryStatus } from 'api/lotteries';
import { EPrizeType } from '../constants';

/** SS gives physical prizes a cash equivalent: only their x1 wager tells them apart */
export const getPrizeType = (prize: LotteryPrize) => {
  if (prize.freespinsCount > 0) return EPrizeType.FREE_SPINS;
  if (prize.wagerMultiplier <= 1) return EPrizeType.PHYSICAL;

  return EPrizeType.CASH;
};

/** the back office may leave stray spaces or another case */
export const normalizeStuff = (stuff: string) => stuff.trim().toLowerCase();

export interface IPrizeGroup {
  key: string;
  /** the best place; the group differs by place and winner only */
  prize: LotteryPrize;
  prizes: LotteryPrize[];
}

export const getPrizeKey = ({
  stuff,
  freespinsCount,
  moneyAwardCents,
}: LotteryPrize) => {
  if (stuff) return `stuff:${normalizeStuff(stuff)}`;
  if (freespinsCount > 0) return `fs:${freespinsCount}`;

  return `cash:${moneyAwardCents}`;
};

export const groupPrizes = (prizes: LotteryPrize[]): IPrizeGroup[] => {
  const groups = new Map<string, LotteryPrize[]>();
  const byPlace = [...prizes].sort((a, b) => a.awardPlace - b.awardPlace);

  for (const prize of byPlace) {
    const key = getPrizeKey(prize);

    groups.set(key, [...(groups.get(key) ?? []), prize]);
  }

  return Array.from(groups, ([key, group]) => ({
    key,
    prize: group[0],
    prizes: group,
  }));
};

/** wonPrizeIds: prizes SS awards without a ticket when prizes outnumber tickets */
export const getWonPrizes = (
  lottery: Lottery,
  status: LotteryStatus | undefined,
  wonPrizeIds: number[]
) => {
  const wonIds = new Set([
    ...Object.values(status?.prizes ?? {}),
    ...wonPrizeIds,
  ]);

  return lottery.prizes.filter(({ id }) => wonIds.has(id));
};

export const hasDrawnWinners = (lottery: Lottery) =>
  lottery.prizes.some(({ nickname, ticket }) => nickname || ticket);
